import { useState, useRef, useEffect, useCallback } from "react";

import {
  EventType,
  InteractionEventTypes,
  SessionDataRequestBody,
  usePostSessionDataMutation,
} from "../redux/services/config";

import { getFailedSessionRequests, setFailedSessionRequests } from "../utils";
import { useQueryParams } from "./useQueryParams";

import { MessageType } from "../types/messagingProtocol";

import { setIsSessionActive } from "../redux/slices/applicationSlice";
import { useDispatch } from "react-redux";

export const useSession = (
  sessionTimeout: number,
  sessionTimeoutNotice: number,
  onSessionTimeout: () => void,
  screenId: string,
) => {
  const { isDebugMode, isTimeoutMode } = useQueryParams();

  const [postSessionData] = usePostSessionDataMutation();

  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutNoticeID = useRef<ReturnType<typeof setTimeout> | null>(null);
  const events = useRef<EventType[]>([]);

  const [isInactive, setIsInactive] = useState(false);
  const isSessionActive = useRef(false);
  const sessionStart = useRef<string | null>(null);

  // We need to store the onSessionTimeout function as a ref, otherwise the timeout creates a closure and only executes the initial function that was passed in, but the function contains a state variable that could change before the timeout executes.
  const handleSessionTimeout = useRef<typeof onSessionTimeout>();

  const dispatch = useDispatch();

  useEffect(() => {
    handleSessionTimeout.current = onSessionTimeout;
  }, [onSessionTimeout]);

  const initialiseTimeout = useCallback(() => {
    console.debug(`Initialising "End session" timeout in ${sessionTimeout}ms`);

    return setTimeout(() => {
      console.debug('Executing "End session" timeout');

      if (handleSessionTimeout.current) {
        handleSessionTimeout.current();
      }

      if (sessionStart.current) {
        const data: SessionDataRequestBody = {
          id: screenId,
          session_start: sessionStart.current,
          session_end: new Date().toISOString(),
          component: "map_primary",
          events: events.current,
        };

        !isDebugMode &&
          postSessionData(data)
            .unwrap()
            .catch((error) => {
              if (error.status !== 400) {
                const pastFailedRequests = getFailedSessionRequests();
                setFailedSessionRequests([...pastFailedRequests, data]);
              }
            });

        events.current = [];
        sessionStart.current = null;

        // check to see if a instance restart is required
        const reloadRequired = localStorage.getItem(MessageType.RELOAD_WEBVIEW);
        if (reloadRequired === "true") {
          localStorage.setItem(MessageType.RELOAD_WEBVIEW, "");
          window.location.reload();
        }
      }

      setIsInactive(false);
      dispatch(setIsSessionActive(false));
      isSessionActive.current = false;
    }, sessionTimeout);
  }, [sessionTimeout, screenId, isDebugMode, postSessionData, dispatch]);

  const initialiseTimeoutNotice = useCallback(() => {
    console.debug(
      `Initialising "Are you still there?" notice timeout in ${sessionTimeoutNotice}ms`,
    );

    return setTimeout(() => {
      console.debug('Executing "Are you still there?" notice timeout');

      setIsInactive(true);
      dispatch(setIsSessionActive(true));
    }, sessionTimeoutNotice);
  }, [sessionTimeoutNotice, dispatch]);

  const checkSessionIsActive = useCallback(() => {
    if (isSessionActive.current) return;

    const sessionStartTime = new Date().toISOString();
    isSessionActive.current = true;
    dispatch(setIsSessionActive(true));
    sessionStart.current = sessionStartTime;
  }, [dispatch]);

  const resetTimeouts = useCallback(() => {
    checkSessionIsActive();

    if (timeoutID.current) clearTimeout(timeoutID.current);
    if (timeoutNoticeID.current) clearTimeout(timeoutNoticeID.current);

    timeoutID.current = initialiseTimeout();
    timeoutNoticeID.current = initialiseTimeoutNotice();

    setIsInactive(false);
  }, [checkSessionIsActive, initialiseTimeout, initialiseTimeoutNotice]);

  const handleTouchEvent = (
    eventType: InteractionEventTypes,
    enableLogging: boolean,
    resetSessionTimeouts: boolean = true,
    extraData?: { [key: string]: any },
  ) => {
    isDebugMode && console.log(eventType, extraData);

    checkSessionIsActive();

    if (enableLogging) {
      events.current = [
        ...events.current,
        {
          timestamp: new Date().toISOString(),
          event_type: eventType,
          ...extraData,
        },
      ];
    }

    if (resetSessionTimeouts) {
      resetTimeouts();
    }
  };

  if (!isTimeoutMode) {
    return {
      handleTouchEvent: () => {},
      isInactive: false,
      resetTimeouts: () => {},
    };
  }

  return {
    handleTouchEvent,
    isInactive,
    resetTimeouts,
  };
};
